import { Box, Divider, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput.tsx";
import RowJsonStructure from "../../../../../components/common/RowJsonStructure/RowJsonStructure.tsx";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import { showErrorToast } from "../../../../../utils/toast";
import TgImagesPreviewThumbstrip from "../components/TgImagesPreviewThumbstrip/TgImagesPreviewThumbstrip";
import styles from "./TgImagesCaptioning.module.scss";

type Props = {
	onTrainModel: () => void;
};

const useSelectedImage = (images: any[], initialIndex: number = 0) => {
	const [selectedIndex, setSelectedIndex] = useState<number>(initialIndex);
	const selected = images[selectedIndex];

	useEffect(() => {
		if (selectedIndex >= images.length) setSelectedIndex(0);
	}, [images.length, selectedIndex]);

	const canPrev = selectedIndex > 0;
	const canNext = selectedIndex < images.length - 1;
	const goPrev = useCallback(() => setSelectedIndex((i) => (i > 0 ? i - 1 : i)), []);
	const goNext = useCallback(() => setSelectedIndex((i) => (i < images.length - 1 ? i + 1 : i)), [images.length]);

	return { selectedIndex, setSelectedIndex, selected, canPrev, canNext, goPrev, goNext };
};

const TgImagesCaptioning = ({ onTrainModel }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.imagesCaptioning" });
	const { tgStore } = useAppStore();
	const { tgDatasetStore } = tgStore;
	const [prompt, setPrompt] = useState<string>("");
	const images = useMemo(
		() => (tgDatasetStore.datasetForm.images || []).slice().sort((a: any, b: any) => a.id - b.id),
		[tgDatasetStore.datasetForm.images],
	);
	const { selectedIndex, setSelectedIndex, selected, canPrev, canNext, goPrev, goNext } = useSelectedImage(images, 0);
	const isLoadingCaptions = !selected?.caption;
	const [uploadingImageId, setUploadingImageId] = useState<number | null>(null);

	useEffect(() => {
		// Reset refine prompt when selection changes
		setPrompt("");
	}, [selectedIndex, selected?.id]);

	const buildPaginationItems = (total: number, selectedIndex: number): (number | "...")[] => {
		if (total <= 4) return Array.from({ length: total }, (_, i) => i);
		if (selectedIndex <= 1) return [0, 1, 2, "...", total - 1];
		if (selectedIndex >= total - 2) return [0, "...", Math.max(total - 3, 0), total - 2, total - 1];
		return [selectedIndex - 1, selectedIndex, selectedIndex + 1, "...", total - 1];
	};

	const shortDescription = useMemo(() => {
		try {
			const vs = selected?.caption || "";
			if (!vs) return "";
			const obj = JSON.parse(vs);
			return typeof obj?.short_description === "string" ? obj.short_description : "";
		} catch {
			return "";
		}
	}, [selected?.caption]);

	const paginationItems = useMemo<(number | "...")[]>(
		() => buildPaginationItems(images.length, selectedIndex),
		[images.length, selectedIndex],
	);

	const updateSelectedImageCaption = async (caption: string, imageId: number) => {
		if (!imageId) return;
		await tgDatasetStore.updateImageCaption(tgDatasetStore.datasetForm.id, imageId, caption);
	};

	const onUploadJson = async (obj: any) => {
		if (!selected) return;
		const selectedId = selected.id;
		setUploadingImageId(selectedId);
		try {
			const newCaption = JSON.stringify(obj);
			await tgDatasetStore.updateImageCaption(tgDatasetStore.datasetForm.id, selectedId, newCaption);
			tgDatasetStore.datasetForm.images = (tgDatasetStore.datasetForm.images || []).map((img: any) =>
				img.id === selectedId ? { ...img, caption: newCaption } : img,
			);
		} catch (e: any) {
			const raw = e?.response?.data?.message;
			let human: string | undefined;
			try {
				const parsed = JSON.parse(raw);
				human = parsed?.errors?.[0]?.msg as string | undefined;
			} catch {
				// ignore parse error
			}
			showErrorToast(human);
		} finally {
			setUploadingImageId(null);
		}
	};

	const refineImageSchema = async () => {
		const selectedId = selected?.id;
		if (!selectedId) return;
		const visualSchema = await tgDatasetStore.refineDatasetDnaSchema(
			prompt,
			selected?.caption || "{}",
			tgDatasetStore.datasetForm.id,
		);
		updateSelectedImageCaption(visualSchema, selectedId);
	};

	const isRefineLoading = tgDatasetStore.loadingUpdateImage || tgDatasetStore.loadingRefineVisualSchema;
	const isRefineDisabled =
		!prompt || isRefineLoading || !selected || uploadingImageId === selected?.id || !selected?.caption;

	return (
		<>
			<Box className={styles.container}>
				<Box className={styles.left}>
					<Typography className={styles.title}>{t("title")}</Typography>
					<Typography className={styles.subtitle}>{t("subtitle")}</Typography>

					<Divider className={styles.divider} />

					<Box className={styles.generalSection}>
						<Typography className={styles.sectionTitle}>{t("generalTitle")}</Typography>
						{!selected?.caption ? (
							<Box
								style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 80 }}
							>
								<CircularProgress color="primary" size={20} />
							</Box>
						) : (
							<Typography className={styles.sectionDescription}>
								{shortDescription || t("generalDescription")}
							</Typography>
						)}
					</Box>

					<Divider className={styles.divider} />

					<Box className={styles.jsonWrap}>
						<RowJsonStructure
							title={t("viewRawJsonTitle")}
							json={selected?.caption || "{}"}
							disableUpload={
								uploadingImageId === selected?.id ||
								tgDatasetStore.loadingRefineVisualSchema ||
								!selected.caption
							}
							disableDownload={tgDatasetStore.loadingRefineVisualSchema || !selected.caption}
							uploadLoading={uploadingImageId === selected?.id}
							onUploadJson={onUploadJson}
						/>
					</Box>

					<Divider className={styles.divider} />

					<Box className={styles.refineWrap}>
						<Box className={styles.refineHeader}>
							<Typography className={styles.sectionTitle}>{t("refineTitle")}</Typography>
						</Box>
						<Box className={styles.refineControls}>
							<BriaInput
								className={styles.refineInput}
								placeholder={t("refinePlaceholder")}
								value={prompt}
								onChange={(e) => setPrompt(e.target.value)}
								height="40px"
								onEnterPress={!isRefineDisabled && !isRefineLoading ? refineImageSchema : undefined}
							/>
							<BriaButton
								buttonType="primaryMedium"
								disabled={isRefineDisabled}
								loading={isRefineLoading}
								onClick={refineImageSchema}
							>
								{t("updateButton")}
							</BriaButton>
						</Box>
					</Box>
				</Box>

				<Box className={styles.right}>
					<TgImagesPreviewThumbstrip
						images={images}
						selectedIndex={selectedIndex}
						setSelectedIndex={setSelectedIndex}
						canPrev={canPrev}
						canNext={canNext}
						goPrev={goPrev}
						goNext={goNext}
						paginationItems={paginationItems}
						isLoadingCaptions={isLoadingCaptions}
						prevLabel={t("prev")}
						nextLabel={t("next")}
					/>
				</Box>
			</Box>

			<Box className={styles.footer}>
				<BriaButton
					buttonType="primaryMedium"
					onClick={onTrainModel}
					disabled={
						!!uploadingImageId ||
						tgDatasetStore.loadingUpdateImage ||
						tgDatasetStore.loadingRefineVisualSchema
					}
				>
					{t("trainModelButton")}
				</BriaButton>
			</Box>
		</>
	);
};

export default observer(TgImagesCaptioning);
